/**
 * Event-detail resource service (GS) — CT-5092.
 *
 * The unified detail / thread / audit-trail endpoints do NOT exist yet; they are
 * owned by CT-4867. In the interim:
 *
 *  - DETAIL is fetched for real via the governance "My Work" preview endpoints,
 *    but only for the two interim sources that expose one:
 *      • claim_suggestion → POST /v1/my-work/claim-suggestion/preview  ({ claimUuid })
 *      • change_request   → POST /v1/my-work/change-request/preview
 *                           ({ changeRequestUuid, knowledgebaseUuid })
 *    (proposal / task have no preview endpoint → no enrichment.)
 *
 *    change-request/preview was 500-broken (MWK500) for a while; it came back
 *    online on 2026-09-01 and now returns 200 with the real body (proposedValueText,
 *    truthCurrentText, truthTitle, operationType, riskLevel, confidence, …). We call
 *    it for real; if it ever errors again the caller surfaces a toast and the base
 *    event's list-row detail keeps the panel populated.
 *
 *  - THREAD, per-event AUDIT TRAIL and POST-COMMENT have NO endpoint. The
 *    functions below exist as the forward-looking seam (so callers can wire to
 *    them today), but resolve to empty / not-implemented until CT-4867 ships.
 *    They intentionally do NOT invent a wrong endpoint (`truth/activity/list` is
 *    a per-KB feed, not a per-event thread or an immutable trail).
 *
 * Field names mirror the proven beta Governance Studio "My Work" wrappers
 * (web-app-governance-studio `src/services/myWork.js`) — the reference for the
 * real GS request/response shapes.
 */

import { gsApi } from './apiClient.js';
import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { INTERIM_EVENT_TYPE } from '../domains/work-queue/constants/interimEventTypes.js';
import { EVENT_TYPE } from '../domains/work-queue/constants/eventTypes.js';
import {
  getPromotionDetails,
  listPromotionClaims,
} from './promotionActions.js';

// NOTE (request keys): the GS `my-work` endpoint family expects camelCase params
// (`claimUuid`, `changeRequestUuid`, `knowledgebaseUuid`) — verified live 2026-08-31
// and matches the proven beta client; snake_case is rejected. Do NOT "correct"
// these to snake_case. (Same deviation documented in services/workQueue.js.)

/** POST + envelope-unwrap. Returns the normalized `{ body, meta, ... }`. */
async function post(path, payload, errorMessage) {
  const res = await gsApi.post(path, payload);
  return validateApiResponse(res, errorMessage);
}

/**
 * Enriched preview for a single claim suggestion (Train Me detail).
 * @param {string} claimUuid
 * @returns {Promise<Object|null>} the preview body, or `null` when unavailable.
 */
export async function previewClaimSuggestion(claimUuid) {
  if (!claimUuid) return null;
  const { body } = await post(
    '/v1/my-work/claim-suggestion/preview',
    { claimUuid },
    'Failed to load claim detail',
  );
  return body ?? null;
}

/**
 * Enriched preview for a change request (Gov Review detail). Live-verified 200 on
 * 2026-09-01 (msgCode TRUTH_CHANGE_REQUEST_PREVIEW_RETRIEVED); it had returned
 * MWK500 before. Still called best-effort: any future failure degrades to the
 * in-memory list-row detail (see {@link getEventDetailPreview}) via the caller's
 * toast, never blocking the panel.
 *
 * @param {{ changeRequestUuid: string, knowledgebaseUuid: string }} params
 * @returns {Promise<Object|null>}
 */
export async function previewChangeRequest({ changeRequestUuid, knowledgebaseUuid } = {}) {
  if (!changeRequestUuid || !knowledgebaseUuid) return null;
  const { body } = await post(
    '/v1/my-work/change-request/preview',
    { changeRequestUuid, knowledgebaseUuid },
    'Failed to load change request detail',
  );
  return body ?? null;
}

/**
 * Enriched preview for a Gov Promotion package: details + paginated claims (CT-5095).
 *
 * TEMPORARY: GET `/v1/workspace/promotion/details|claims/{uuid}` until the unified
 * My Work promotion detail endpoint ships (CT-4867).
 *
 * @param {string} promotionUuid
 * @returns {Promise<{ details: Object|null, claims: Object[] }|null>}
 */
export async function previewGovPromotion(promotionUuid) {
  if (!promotionUuid) return null;
  const [details, claimsResult] = await Promise.all([
    getPromotionDetails(promotionUuid),
    listPromotionClaims(promotionUuid, { pageSize: 100 }),
  ]);
  return {
    details: details ?? null,
    claims: claimsResult?.items ?? [],
  };
}

/**
 * Routes an inbox event to its per-type detail preview. Returns `null` (no
 * enrichment) for sources without a preview endpoint, and swallows the known
 * change-request-preview server error so a flaky endpoint never breaks the
 * detail panel — the base in-memory event still renders.
 *
 * @param {Object} event - a UI event (from the interim fan-out).
 * @returns {Promise<Object|null>} the raw preview body, or `null`.
 */
/**
 * True when opening `event` will actually hit the network for a detail preview —
 * claim suggestions and change requests (both previews are live). Lets callers
 * skip a loading state that would otherwise flash for proposal / task (no preview
 * endpoint).
 */
export function eventHasDetailPreview(event) {
  if (!event) return false;
  const detail = event.detail ?? {};
  switch (event.type) {
    case INTERIM_EVENT_TYPE.CLAIM_SUGGESTION:
      return Boolean(detail.claimUuid ?? detail.claim_uuid);
    case INTERIM_EVENT_TYPE.CHANGE_REQUEST:
      return Boolean(
        (detail.changeRequestUuid ?? detail.change_request_uuid) &&
          (detail.knowledgebaseUuid ?? detail.knowledgebase_uuid),
      );
    case EVENT_TYPE.GOV_PROMOTION:
      return Boolean(detail.promotionUuid ?? detail.promotion_uuid);
    default:
      return false;
  }
}

export async function getEventDetailPreview(event) {
  if (!event) return null;
  const detail = event.detail ?? {};

  switch (event.type) {
    case INTERIM_EVENT_TYPE.CLAIM_SUGGESTION:
      // Verified 200 (CLM_PREVIEW_OK) against real GS on 2026-08-31.
      return previewClaimSuggestion(detail.claimUuid ?? detail.claim_uuid);
    case INTERIM_EVENT_TYPE.CHANGE_REQUEST:
      // Live-verified 200 (TRUTH_CHANGE_REQUEST_PREVIEW_RETRIEVED) on 2026-09-01 —
      // it was MWK500 before. Enriches the detail with the real body
      // (proposedValueText, truthCurrentText, truthTitle, operationType, riskLevel,
      // confidence, …). If it ever errors again, the caller toasts it and the base
      // event still renders from the change-request LIST row (in `detail.raw`).
      return previewChangeRequest({
        changeRequestUuid: detail.changeRequestUuid ?? detail.change_request_uuid,
        knowledgebaseUuid: detail.knowledgebaseUuid ?? detail.knowledgebase_uuid,
      });
    case EVENT_TYPE.GOV_PROMOTION:
      return previewGovPromotion(detail.promotionUuid ?? detail.promotion_uuid);
    default:
      // proposal / task: no preview endpoint yet (CT-4867).
      return null;
  }
}

// ── Blocked on CT-4867 ───────────────────────────────────────────────────────
// The three functions below are the seam the ticket asks for. No GS endpoint
// backs them yet, so they resolve to empty. Wire them to the real endpoints
// (and drop these stubs) once CT-4867 ships the thread / audit / comment APIs.

/**
 * Per-event conversation thread. BLOCKED ON CT-4867 (no endpoint) — resolves to
 * an empty open thread. Until then the live thread state is the provider's
 * in-memory optimistic store (action flows append to it).
 */
export async function getEventThread(/* eventId */) {
  return { status: 'open', comments: [] };
}

/**
 * Per-event immutable audit trail. BLOCKED ON CT-4867 (no per-event endpoint;
 * `truth/activity/list` is a per-KB feed) — resolves to an empty list.
 */
export async function getEventAuditTrail(/* eventId */) {
  return [];
}

/**
 * Posts a comment to an event thread. BLOCKED ON CT-4867 — there is no
 * persistence endpoint, so this rejects. Callers keep an optimistic local append
 * for the interim (see WorkQueueProvider.postThreadComment) and surface this
 * rejection as an error once the real endpoint is wired.
 */
export async function postEventComment(/* { eventId, body } */) {
  const error = new Error('Posting comments is not available yet (blocked on CT-4867).');
  error.code = 'NOT_IMPLEMENTED';
  throw error;
}

// Close / reopen / mark-read live in {@link threadActions.js} — same CT-4867
// blocker, mocked locally until the thread endpoint ships.
